import React, { useEffect, useMemo, useState } from 'react';
import { get } from 'lodash';
import { connect } from 'react-redux';
import Markdown from 'react-commonmark';
import { animateScroll } from 'react-scroll';
import { Row, Col, Alert } from 'react-bootstrap';
import { actions as productActions } from '../../../../redux/productActions';
import { hasPermission, isBroker } from '../../../../../auth/redux/authActions';
import { Field, reduxForm, reset, getFormValues } from 'redux-form';
import displayCurrency from '../../../../../../helpers/displayCurrency';
import getIncludedResource from '../../../../../../helpers/getIncludedResource';
import ButtonBar from '../../../../../../common/components/ButtonBar/ButtonBar';
import {
  Button,
  HorizontalFormControl,
  PaymentOptions,
} from '../../../../../../common/components';
import CustomerTransactions from '../../../../../customer/components/CustomerTransactions/CustomerTransactions';
import { CheckboxGroup } from '../../../../../../common/components/ReduxFormField';
import './styles/TabContent.scss';
import getPaymentOptions from '../../../../../../helpers/getPaymentOptions';
import PaymentIFrame from '../../../../../../common/components/PaymentIFrame/PaymentIFrame';
import { required } from 'redux-form-validators';
import getOwnerBrokerType from '../../../../../../helpers/getOwnerBrokerType';
import { Refund } from '../../../Refund/Refund';
import {
  Divider
} from "../../../../../brochure/components/StyledComponents/StyledComponents";

const FORM_NAME = 'IssuePolicyDialog';
const scrollToTop = () => {
  const scroll = animateScroll;
  scroll.scrollTo(30);
};

const getErrors = (validationErrors) => {
  const errors = [];
  if (validationErrors) {
    Object.keys(validationErrors).map((errorKey) => {
      if (!validationErrors[errorKey]) {
        errors.push(errorKey + ' required');
      }
    });
  }
  return errors;
};

const Error = ({ message }) => {
  if (!message) {
    return null;
  }
  return <Alert bsStyle={'danger'}>PaymentError: {message}</Alert>;
};

const PaymentOptionsContainer = ({
  amount,
  salesChannel,
  brokerType,
  paymentType,
  submitFailed,
  // onOpenPaymentMethod,
}) => {
  const paymentTitleStyle = ['pay-method__title', submitFailed && 'pay-method--error'];

  const organisationType = isBroker()
    ? 'Broker'
    : getOwnerBrokerType(salesChannel, brokerType);

  if (amount > 0) {
    return (
      <Row>
        <Col sm={12}>
          <div className="pay-method">
            <div className={paymentTitleStyle.join(' ')}>
              Please select a payment method:
            </div>
          </div>
          <PaymentOptions
            // handlePaymentChange={onOpenPaymentMethod}
            selectedButtonType={paymentType}
            paymentOptions={getPaymentOptions(organisationType)}
            validate={required({ msg: 'Payment Type is required' })}
          />
        </Col>
      </Row>
    );
  }
  return null;
};

const Declaration = ({ mta, declaration, channelType, premium }) => {
  const channelDeclaration = get(declaration, channelType);
  const transformDocLinks = (url) => {
    const documents = premium.attributes.scheme.documents;

    if (url === 'policy_wording') {
      return documents['Policy Wording'];
    } else if (url === 'policy_summary') {
      return documents['Policy Summary'];
    } else if (url === 'tobca') {
      return documents['Terms of Business Customer Agreement'];
    }

    return url;
  };

  if (!mta && channelDeclaration) {
    return (
      <div className={'payment-tab-declaration'}>
        <Row>
          <Col sm={12}>
            <h4>Declaration</h4>

            <div>
              <div className="declaration">
                <Markdown
                  linkTarget="_blank"
                  source={channelDeclaration}
                  transformLinkUri={transformDocLinks}
                />
              </div>
            </div>
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <Field
              name={'meta.purchase_declaration'}
              inline
              options={[
                {
                  label: 'Please tick to confirm you agree to the above declaration',
                  value: true,
                },
              ]}
              component={CheckboxGroup}
            />
          </Col>
        </Row>
      </div>
    );
  }
  return null;
};

const PaymentTab06 = ({
  isMta,
  applicationFormValues,
  declaration,
  channelType,
  redirectToIssued,
  onUpdateTab,
  prepareApplication,
  issuePolicy,
  formValues,
  handleSubmit,
  submitting,
  submitFailed,
  dispatch,
  change,
}) => {
  const [cardError, setCardError] = useState();
  const validationErrors = applicationFormValues.data.attributes.validation;

  const applicationErrors = useMemo(() => getErrors(validationErrors), [
    validationErrors,
  ]);
  const premiums = getIncludedResource(
    applicationFormValues.data,
    applicationFormValues.included,
    'premiums',
  );
  const selectedPremium = premiums.length === 1 ? premiums?.[0] : undefined;
  const agreed = formValues?.meta.purchase_declaration;
  const gross = selectedPremium?.attributes?.gross;
  const taxRate = selectedPremium?.attributes.rates.tax_rate;
  const isRefund = formValues?.meta.refund;
  const paymentType = formValues?.meta.payment_type;

  useEffect(
    () => {
      onUpdateTab(6);
      scrollToTop();
    },
    [onUpdateTab],
  );

  useEffect(
    () => {
      if (isMta && gross < 0) {
        if (!isRefund) {
          dispatch(change('meta.payment_type', 'no refund'));
        }
        if (isRefund && gross < 0) {
          dispatch(change('meta.payment_type', ''));
        }
      }
    },
    [change, dispatch, gross, isRefund, isMta],
  );

  useEffect(
    () => {
      if (!agreed) {
        dispatch(reset(FORM_NAME));
      }
    },
    [agreed, dispatch],
  );

  if (!selectedPremium) {
    return <div> cannot find premium</div>;
  }

  const issue = (values) => {
    const premiumId = values?.data?.relationships?.premium?.data?.id;
    if (!applicationFormValues || !applicationFormValues.data.id) {
      return false;
    }

    if (paymentType === 'card') {
      return prepareApplication(applicationFormValues.data.id, premiumId);
    } else {
      return issuePolicy(applicationFormValues.data.id, values);
    }
  };

  const broker = getIncludedResource(
    applicationFormValues.data,
    applicationFormValues.included,
    'broker',
  );

  const customer = getIncludedResource(
    applicationFormValues.data,
    applicationFormValues.included,
    'customer',
  );

  if (applicationErrors.length > 0) {
    return (
      <Row>
        <Col sm={12}>
          <h4>Not ready for issuing</h4>
          {applicationErrors.length > 0 && (
            <div>
              <p>Please resolve the following issues:</p>
              <ul>
                {applicationErrors.map((errorItem, i) => (
                  <li key={i}>{errorItem}</li>
                ))}
              </ul>
            </div>
          )}
        </Col>
      </Row>
    );
  }
  return (
    <div className={'payment-details-tab'}>
      <Declaration
        channelType={channelType === 'B2C' ? 'DIR' : channelType}
        mta={isMta}
        declaration={declaration}
        premium={selectedPremium}
      />

      {agreed && (
        <div className={'payment-details'}>
          <Row>
            <Col sm={12}>
              <h4>Payment Details</h4>
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <div className="total-premium">
                <span>Total to pay:</span>
                <span className="total-premium red">
                  &nbsp;
                  {displayCurrency(gross)}
                  <span style={{ fontSize: '0.8em' }}>
                    &nbsp;including {taxRate}% Insurance Premium Tax (IPT)
                  </span>
                </span>
              </div>
            </Col>
          </Row>

          <Refund isRefund={isRefund} show={isMta && gross < 0} />

          <PaymentOptionsContainer
            amount={formValues?.meta.amount}
            salesChannel={applicationFormValues?.data?.attributes.metadata.sales_channel}
            brokerType={broker.data?.attributes?.broker_type}
            paymentType={formValues?.meta.payment_type}
            submitFailed={submitFailed}
          />

          <Error message={cardError} />
          <PaymentIFrame onSuccess={redirectToIssued} onFail={setCardError} />
          <div className="payment-options-container">
            {(paymentType === 'cash' || paymentType === 'cheque') && (
              <div className="card-details">
                <Row>
                  <Col sm={8} className="fadein-fields">
                    <Field
                      labelSize={12}
                      fieldSize={4}
                      paddingLabelTitle
                      className="text-center"
                      label="Payment Reference"
                      name={'meta.payment_reference'}
                      component={HorizontalFormControl}
                    />
                  </Col>
                </Row>
              </div>
            )}
            <ButtonBar>
              <Divider />
              <Button
                type="submit"
                id={'issue-policy-button'}
                bsStyle="primary"
                isLoading={submitting}
                className={'pull-right button-action-issue'}
                label={isMta ? 'Issue MTA' : 'Issue Policy'}
                handleClick={handleSubmit(issue)}
              />
            </ButtonBar>
          </div>
        </div>
      )}
      {customer && isMta && hasPermission('policy.underwriter') && (
        <div>
          <CustomerTransactions
            hideTitle
            removePadding
            hideSizePerPage
            disableMinHeight
            resource={customer}
          />
        </div>
      )}
    </div>
  );
};

const form = reduxForm({ form: FORM_NAME })(PaymentTab06);

export default connect(
  (state, props) => {
    const smsNumber =
      props?.applicationFormValues?.data?.attributes.metadata?.adults[0].phone2;
    const sendSMS =
      !!smsNumber &&
      !!props?.applicationFormValues?.data?.attributes.metadata?.communications?.sms;
    const email =
      props?.applicationFormValues?.data?.attributes.metadata?.adults[0].email;
    const sendEmail =
      isBroker() &&
      !!email &&
      !!props?.applicationFormValues?.data?.attributes.metadata?.communications?.email;

    const premiums = getIncludedResource(
      props.applicationFormValues.data,
      props.applicationFormValues.included,
      'premiums',
    );
    const premium = premiums.length === 1 ? premiums?.[0] : undefined;

    return {
      initialValues: {
        data: {
          relationships: {
            premium: {
              data: {
                type: 'premium',
                id: premium?.id,
              },
            },
          },
        },
        meta: {
          product_code: props.productCode,
          payment_type: '',
          previous_card: false,
          send_sms: sendSMS,
          sms_number: sendSMS ? smsNumber : '',
          send_email: sendEmail,
          refund: true,
          purchase_declaration: !!props.isMta,
          amount: premium?.attributes?.gross ?? 0,
        },
      },
      formValues: getFormValues(FORM_NAME)(state),
    };
  },
  {
    prepareApplication: productActions.prepareApplication,
    issuePolicy: productActions.issuePolicy,
  },
)(form);
